import React, {useEffect, useId, useRef} from 'react'
import {VStack, HStack, Text, Image, useMultiStyleConfig, StackProps, TextProps, LinkProps, Link, Skeleton, IconButtonProps, FocusLock} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import {Icon, DarkOverlay, IconButton} from '_uiComponents'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

type ToastStatus = 'info' | 'warning' | 'success' | 'error' | 'loading' | undefined

interface ToastProps {
    title: string
    productImg?: string
    wishlistLabel: string
    isUserLogged?: boolean
    status: ToastStatus
    closeLabel: string
    children: string
    onClose: () => void
}

/**
 * @name Toast
 *
 * @component
 *
 * @description
 * Displays the wishlist add/remove confirmation with optional product image and wishlist link.
 *
 * It behaves as a modal dialog: it stays open until the user dismisses it (close button, Escape, or a
 * click outside on the DarkOverlay backdrop) — never auto-dismissing (WCAG 2.2 SC 2.2.1). Following the
 * Modal Dialog pattern, focus moves into it on open, is trapped while open, and returns to the trigger on
 * close; the container exposes role="dialog" with an accessible name from its heading.
 *
 * @param {ToastProps} props - The props for the Toast component.
 * @param {string} props.title - Title of the message to be displayed in the toast.
 * @param {string} [props.productImg] - Product image url.
 * @param {string} props.wishlistLabel - Label to be displayed in the wishlist button.
 * @param {boolean} [props.isUserLogged=false] - Indicates if the user is logged or not.
 * @param {ToastStatus} props.status - Status of the toast.
 * @param {string} props.children - Description to be displayed in the toast.
 * @param {function} props.onClose - On close function.
 *
 * @returns {React.FC<ToastProps>} - The Toast component.
 */
const Toast: React.FC<ToastProps> = ({title, productImg, wishlistLabel, isUserLogged = false, status = 'success', closeLabel, children, onClose}) => {
    const styles = useMultiStyleConfig('Toast') as Record<string, StackProps & TextProps & LinkProps & IconButtonProps>
    const {locale} = useMultiSite()

    const closeBtnRef = useRef<HTMLButtonElement | null>(null)
    const prevFocusedElement = useRef<HTMLElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const titleId = useId()

    const wishlistHref = isUserLogged ? `/${locale.alias}/account?tabID=wishlist` : `/${locale.alias}/wishlist`

    // Now that the confirmation stays open until the user dismisses it (close button or click outside on
    // the DarkOverlay backdrop) and traps focus, it behaves as a modal dialog — so it follows the Modal
    // Dialog a11y pattern rather than the ephemeral "toast" one: focus moves into it on open, is trapped
    // while open (FocusLock), and returns to the trigger on close; it is closable via keyboard (Escape).
    useEffect(() => {
        // Store the previously focused element so focus can be restored to the trigger on close.
        prevFocusedElement.current = document.activeElement as HTMLElement
        // Move focus into the dialog on open (close button — the first actionable control).
        closeBtnRef.current?.focus()
        return () => {
            // Restore focus to the previously focused element on close (Modal Dialog: return focus).
            prevFocusedElement.current?.focus()
        }
    }, [])

    // Close on Escape (Modal Dialog: keyboard dismissal / best practice). No auto-dismiss on timer or on
    // focus leaving the container — the confirmation persists until an explicit user dismissal.
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
            onClose()
        }
    }

    return (
        <>
            <VStack
                {...styles.wrapper}
                ref={containerRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onKeyDown={handleKeyDown}
            >
                <FocusLock persistentFocus={false}>
                    <HStack {...styles.titleWrapper}>
                        <Text id={titleId} {...styles.title}>
                            {title}
                        </Text>
                        <IconButton
                            size="iconButtonMd"
                            ref={closeBtnRef}
                            aria-label={closeLabel}
                            onClick={onClose}
                            icon={<Icon name="IconClose" size="md" />}
                        />
                    </HStack>

                    <HStack {...styles.bodyWrapper}>
                        {productImg && <Image fallback={<Skeleton {...styles.productImageSkeleton} />} src={productImg} alt={title} {...styles.productImage} />}

                        <VStack {...styles.info}>
                            <Text {...styles.message} {...(status === 'error' ? styles.errorMessage : {})}>
                                {children}
                            </Text>

                            {isUserLogged ? (
                                <Link variant="alwaysUnderline" {...styles.wishlistLink} href={wishlistHref} onClick={onClose}>
                                    {wishlistLabel}
                                </Link>
                            ) : (
                                <Link
                                    variant="alwaysUnderline"
                                    as={RouteLink}
                                    {...styles.wishlistLink}
                                    to={{pathname: wishlistHref, state: {scrollToTop: true}}}
                                    onClick={onClose}
                                >
                                    {wishlistLabel}
                                </Link>
                            )}
                        </VStack>
                    </HStack>
                </FocusLock>
            </VStack>
            <DarkOverlay onClick={onClose} />
        </>
    )
}

export {Toast}
